import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  faCalendarDay,
  faChartLine,
  faComments,
  faGlobe,
  faMessage,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { compactNumber, formatDate } from "../utils/format";
import { EmptyState, ErrorState, PageHeader, Skeleton } from "../components/UI";

export default function AnalyticsPage() {
  const { websiteId } = useParams();
  const [range, setRange] = useState(30);
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    const path = websiteId
      ? `/analytics/websites/${websiteId}`
      : "/analytics/dashboard";
    const params = websiteId
      ? {
          from: new Date(Date.now() - (range - 1) * 86400000)
            .toISOString()
            .slice(0, 10),
        }
      : {};
    api
      .get(path, { params })
      .then((r) => setState({ loading: false, data: getData(r), error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: e.message }));
  }, [websiteId, range]);
  useEffect(load, [load]);
  const chartData = useMemo(
    () =>
      state.data
        ? websiteId
          ? state.data.daily.map((d) => ({
              date: d.date,
              sessions: d.totalSessions,
              messages: d.totalMessages,
              visitors: d.uniqueVisitors,
            }))
          : state.data.trend
        : [],
    [state.data, websiteId],
  );
  if (state.loading) return <Skeleton rows={8} />;
  if (state.error) return <ErrorState message={state.error} onRetry={load} />;
  const data = state.data;
  const summary = websiteId ? data.summary : data;
  const cards = websiteId
    ? [
        { label: "Sessions", value: summary.sessions, icon: faComments },
        { label: "Messages", value: summary.messages, icon: faMessage },
        { label: "Unique visitors", value: summary.visitors, icon: faUsers },
        {
          label: "Messages / session",
          value: summary.averageMessagesPerSession,
          icon: faChartLine,
        },
      ]
    : [
        {
          label: "Total websites",
          value: summary.totalWebsites,
          icon: faGlobe,
        },
        {
          label: "Active websites",
          value: summary.activeWebsites,
          icon: faChartLine,
        },
        { label: "Sessions", value: summary.totalSessions, icon: faComments },
        { label: "Messages", value: summary.totalMessages, icon: faMessage },
      ];
  const activeDay = chartData.length
    ? [...chartData].sort((a, b) => b.messages - a.messages)[0]
    : null;
  return (
    <>
      <PageHeader
        eyebrow={websiteId ? "Tenant analytics" : "Portfolio analytics"}
        title={
          websiteId ? `${data.website.name} activity` : "Conversation analytics"
        }
        description={
          websiteId
            ? `Privacy-conscious activity for ${data.website.domain}.`
            : "Measure assistant adoption and question volume across every website."
        }
        actions={
          websiteId && (
            <label className="range-control">
              Range
              <select
                value={range}
                onChange={(e) => setRange(Number(e.target.value))}
              >
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </label>
          )
        }
      />
      <section className="kpi-grid">
        {cards.map((card, i) => (
          <article className="kpi-card" key={card.label}>
            <span className={`signal-rail rail-${i}`} />
            <div className="kpi-icon">
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <span>{card.label}</span>
            <strong>{compactNumber(card.value)}</strong>
          </article>
        ))}
      </section>
      <section className="analytics-grid">
        <article className="panel chart-panel">
          <header>
            <div>
              <span className="eyebrow">Activity trend</span>
              <h2>{websiteId ? `${range}-day performance` : "Last 30 days"}</h2>
            </div>
          </header>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="analytics-fill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0" stopColor="#556ee6" stopOpacity={0.24} />
                    <stop offset="1" stopColor="#556ee6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="#e8ebf1"
                  vertical={false}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => formatDate(v, true)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip labelFormatter={(v) => formatDate(v)} />
                <Area
                  dataKey="messages"
                  type="monotone"
                  stroke="#556ee6"
                  fill="url(#analytics-fill)"
                  strokeWidth={2.5}
                />
                <Area
                  dataKey="sessions"
                  type="monotone"
                  stroke="#39a981"
                  fillOpacity={0}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              icon={faChartLine}
              title="No activity in this range"
              message="Analytics will populate as visitors ask questions."
            />
          )}
        </article>
        {websiteId ? (
          <aside className="panel top-questions">
            <span className="eyebrow">Intent signals</span>
            <h2>Top questions</h2>
            {data.topQuestions.length ? (
              <ol>
                {data.topQuestions.map((item, index) => (
                  <li key={item.question}>
                    <span>{index + 1}</span>
                    <p>{item.question}</p>
                    <strong>{item.count}</strong>
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyState
                icon={faMessage}
                title="No questions yet"
                message="Common visitor questions will appear here."
              />
            )}
            <div className="active-day">
              <FontAwesomeIcon icon={faCalendarDay} />
              <span>
                <small>Most active day</small>
                <strong>{activeDay ? formatDate(activeDay.date) : "—"}</strong>
              </span>
            </div>
          </aside>
        ) : (
          <aside className="panel pulse-panel">
            <span className="eyebrow">Question windows</span>
            <h2>Recent volume</h2>
            {[
              ["Today", data.questionsToday],
              ["This week", data.questionsWeek],
              ["This month", data.questionsMonth],
            ].map(([label, value]) => (
              <div className="pulse-row" key={label}>
                <span>{label}</span>
                <strong>{compactNumber(value)}</strong>
              </div>
            ))}
          </aside>
        )}
      </section>
    </>
  );
}
